import React, { useEffect, useRef, useState } from 'react';
import { playCountdownBeep, stopCountdownBeep } from '../utils/sound';

const RefereeShootout = React.memo(({ gameState, startShotTimer, stopShotTimer, resetShotTimer, startTimer, pauseTimer, resetTimer, toggleTimerOwner, toggleTimerSync, toggleTimerSound, formatShotTime, formatTime, calculateShotRemaining, calculateCurrentTime }) => {
    // Local display time that ticks every second
    const [displayGlobalTime, setDisplayGlobalTime] = useState(() => formatTime(calculateCurrentTime()));
    const [displayShotTimeSec, setDisplayShotTimeSec] = useState(() => {
        const rem = calculateShotRemaining();
        return Math.max(0, Math.ceil(rem / 1000));
    });

    const activeLimitMs = Number.isFinite(gameState.currentShotLimitMs) && gameState.currentShotLimitMs > 0
        ? gameState.currentShotLimitMs
        : (calculateCurrentTime() >= 300000 ? 10000 : 15000);
    const maxShotTime = Math.round(activeLimitMs / 1000);

    // Sound Logic
    const lastBeepTime = useRef(null);
    useEffect(() => {
        if (!gameState.timerSoundEnabled) {
            stopCountdownBeep();
            return;
        }
        if (displayShotTimeSec > 5 || displayShotTimeSec === 0) {
            stopCountdownBeep();
            lastBeepTime.current = null;
            return;
        }
        if (displayShotTimeSec <= 5 && displayShotTimeSec > 0 && displayShotTimeSec !== lastBeepTime.current) {
            if (displayShotTimeSec === 5) {
                playCountdownBeep();
            }
            lastBeepTime.current = displayShotTimeSec;
        }
    }, [displayShotTimeSec, gameState.timerSoundEnabled]);

    // Sync global timer display every second
    useEffect(() => {
        const interval = setInterval(() => {
            setDisplayGlobalTime(formatTime(calculateCurrentTime()));
        }, 1000);
        return () => clearInterval(interval);
    }, [calculateCurrentTime, formatTime]);

    // Sync shot timer display every second
    useEffect(() => {
        const interval = setInterval(() => {
            const rem = calculateShotRemaining();
            const sec = Math.max(0, Math.ceil(rem / 1000));
            setDisplayShotTimeSec(sec);
        }, 1000);
        return () => clearInterval(interval);
    }, [calculateShotRemaining]);

    const activeTimerPlayer = gameState.timerOwner === 1 ? gameState.firstPlayer : gameState.secondPlayer;
    const activeTimerPlayerId = gameState.timerOwner;

    const copyToClipboard = (viewName) => {
        const url = window.location.href.replace('referee-shootout', viewName);
        navigator.clipboard.writeText(url).then(() => {
            alert('URL copied to clipboard!');
        }).catch(err => {
            console.error('Failed to copy: ', err);
        });
    };

    return (
        <div style={{ width: '100vw', height: '100vh', background: '#2c2c2c', margin: 0, padding: 0, fontFamily: 'Arial, sans-serif', display: 'flex', flexDirection: 'column' }}>
            <div style={{ background: '#1a1a1a', padding: '15px', textAlign: 'center', fontWeight: 'bold', fontSize: '20px', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', borderBottom: '1px solid #333' }}>
                <button
                    onClick={() => window.history.back()}
                    style={{ position: 'absolute', left: '15px', background: 'transparent', border: 'none', color: 'white', fontSize: '24px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                >
                    ←
                </button>
                Referee (Shoot-out)
            </div>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '20px', gap: '20px', overflowY: 'auto', width: '100%', maxWidth: '900px', margin: '0 auto' }}>
                {/* Timer Control Card */}
                <div style={{ background: '#1a1a1a', padding: '15px', borderRadius: '10px', display: 'flex', flexDirection: 'column', gap: '15px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #333', paddingBottom: '10px' }}>
                        <div style={{ color: 'white', fontWeight: 'bold', textAlign: 'center' }}>Timer Control</div>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button
                                onClick={() => {
                                    const url = window.location.href.replace('referee-shootout', 'display-shootout-info');
                                    navigator.clipboard.writeText(url);
                                    alert('Info Board URL copied to clipboard!');
                                }}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#00d2ff', fontSize: '16px', padding: '5px' }}
                                title="Copy Info Board URL"
                            >
                                📋
                            </button>
                            <button
                                onClick={() => {
                                    const url = window.location.href.replace('referee-shootout', 'display-shootout-info');
                                    window.open(url, '_blank');
                                }}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#00d2ff', fontSize: '16px', padding: '5px' }}
                                title="Open Info Board"
                            >
                                🖥️
                            </button>
                        </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ color: '#aaa', fontSize: '14px' }}>Active Timer Player:</span>
                        <span style={{ fontSize: '18px', fontWeight: 'bold', color: activeTimerPlayerId === 1 ? '#4169e1' : '#dc3545' }}>
                            {activeTimerPlayer}
                        </span>
                    </div>
                    <button onClick={toggleTimerOwner} style={{ ...buttonStyle, flexDirection: 'row', justifyContent: 'center', height: '60px', background: '#333' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                            <span style={{ color: activeTimerPlayerId === 1 ? '#4169e1' : '#666', fontWeight: activeTimerPlayerId === 1 ? 'bold' : 'normal' }}>{gameState.firstPlayer}</span>
                            <div style={{ ...buttonIconStyle, background: '#6f42c1', width: '30px', height: '30px', fontSize: '12px' }}>↔</div>
                            <span style={{ color: activeTimerPlayerId === 2 ? '#dc3545' : '#666', fontWeight: activeTimerPlayerId === 2 ? 'bold' : 'normal' }}>{gameState.secondPlayer}</span>
                        </div>
                    </button>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'center', color: 'white', marginTop: '5px' }}>
                        <input
                            type="checkbox"
                            checked={gameState.timerSyncEnabled}
                            onChange={toggleTimerSync}
                            style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                        />
                        <label onClick={toggleTimerSync} style={{ cursor: 'pointer', userSelect: 'none' }}>Sync Timer with Player Switch</label>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'center', color: 'white', marginTop: '5px' }}>
                        <input
                            type="checkbox"
                            checked={gameState.timerSoundEnabled}
                            onChange={toggleTimerSound}
                            style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                        />
                        <label onClick={toggleTimerSound} style={{ cursor: 'pointer', userSelect: 'none' }}>Enable Countdown Sound (5s)</label>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                    {/* Shot Timer Card */}
                    <div style={{ background: '#1a1a1a', padding: '15px', borderRadius: '10px', color: 'white', display: 'flex', flexDirection: 'column', gap: '15px', flex: 1, minWidth: '300px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #333', paddingBottom: '10px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <span style={{ fontSize: '18px', fontWeight: 'bold' }}>Shot Timer</span>
                                <button onClick={() => copyToClipboard('display-shot-timer')} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#888', fontSize: '16px' }} title="Copy URL">
                                    📋
                                </button>
                                <button
                                    onClick={() => {
                                        const url = window.location.href.replace('referee-shootout', 'display-shot-timer');
                                        window.open(url, '_blank');
                                    }}
                                    style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#00d2ff', fontSize: '16px' }}
                                    title="Open Shot Timer"
                                >
                                    🖥️
                                </button>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                                <span style={{ fontSize: '24px', fontWeight: 'bold', color: displayShotTimeSec <= 5 ? '#dc3545' : '#28a745', fontFamily: 'monospace' }}>
                                    {displayShotTimeSec}s
                                </span>
                                <span style={{ fontSize: '14px', color: '#888', marginLeft: '5px' }}>/ {maxShotTime}s</span>
                            </div>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
                            <button onClick={startShotTimer} style={buttonStyle}>
                                <div style={{ ...buttonIconStyle, background: '#28a745' }}>ST</div>
                                <div style={buttonLabelStyle}>START SHOT</div>
                            </button>
                            <button onClick={stopShotTimer} style={buttonStyle}>
                                <div style={{ ...buttonIconStyle, background: '#dc3545' }}>SS</div>
                                <div style={buttonLabelStyle}>STOP SHOT</div>
                            </button>
                            <button onClick={resetShotTimer} style={buttonStyle}>
                                <div style={{ ...buttonIconStyle, background: '#17a2b8' }}>RS</div>
                                <div style={buttonLabelStyle}>RESET SHOT</div>
                            </button>
                        </div>
                    </div>
                    {/* Global Timer Card */}
                    <div style={{ background: '#1a1a1a', padding: '15px', borderRadius: '10px', color: 'white', display: 'flex', flexDirection: 'column', gap: '15px', flex: 1, minWidth: '300px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #333', paddingBottom: '10px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <span style={{ fontSize: '18px', fontWeight: 'bold' }}>Global Timer</span>
                                <button onClick={() => copyToClipboard('display-global-timer')} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#888', fontSize: '16px' }} title="Copy URL">
                                    📋
                                </button>
                                <button
                                    onClick={() => {
                                        const url = window.location.href.replace('referee-shootout', 'display-global-timer');
                                        window.open(url, '_blank');
                                    }}
                                    style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#00d2ff', fontSize: '16px' }}
                                    title="Open Global Timer"
                                >
                                    🖥️
                                </button>
                            </div>
                            <span style={{ fontSize: '24px', fontWeight: 'bold', fontFamily: 'monospace' }}>{displayGlobalTime}</span>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
                            <button onClick={startTimer} style={buttonStyle}>
                                <div style={{ ...buttonIconStyle, background: '#28a745' }}>▶</div>
                                <div style={buttonLabelStyle}>START</div>
                            </button>
                            <button onClick={pauseTimer} style={buttonStyle}>
                                <div style={{ ...buttonIconStyle, background: '#ffc107' }}>⏸</div>
                                <div style={buttonLabelStyle}>PAUSE</div>
                            </button>
                            <button onClick={resetTimer} style={buttonStyle}>
                                <div style={{ ...buttonIconStyle, background: '#17a2b8' }}>⏹</div>
                                <div style={buttonLabelStyle}>RESET</div>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
});

const buttonStyle = {
    background: '#333',
    border: '2px solid #555',
    borderRadius: '10px',
    padding: '10px',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '5px',
    transition: 'all 0.2s',
    minHeight: '70px',
    color: 'white'
};
const buttonIconStyle = {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '16px',
    fontWeight: 'bold',
    color: 'white'
};
const buttonLabelStyle = {
    fontSize: '10px',
    fontWeight: 'bold',
    textAlign: 'center',
    color: '#ccc'
};

export default RefereeShootout;